import { useParams } from 'react-router';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Alert, Button, Flex, Form, Typography } from 'antd';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  ENROLL_PRECHARGEOFF_SETTLEMENT_OFFER,
  CREATE_PRECHARGEOFF_SETTLEMENT_PAYMENT_PLAN,
} from 'mutations/prechargeoff';
import { SIZE, STATUS } from 'utilities/constants';

import PrecoEFTAForm from './PrecoEFTAForm';
import {
  getPrecoErrorReason,
  getPrecoPlanCreationMessage,
} from './precoErrors';
import {
  buildEnrollVariables,
  precoPlanCreationErrorVar,
  resetPrecoState,
  usePrecoState,
} from './precoState';

const { Text, Title } = Typography;

const PrecoEFTAStep = ({ onContinue = () => {} }) => {
  const { customerId, accountId } = useParams();
  const client = useApolloClient();

  const { customerInfo } = useCustomerInfo();
  const { offer, fundingAccount, cadence, schedule } = usePrecoState();

  const fundingAccountLast4 = fundingAccount?.name?.slice(-4);

  const isEnrollingOffer = !offer.offerId;

  const [eftaForm] = Form.useForm();

  const exitToOfferDetails = () => {
    client.refetchQueries({
      include: isEnrollingOffer
        ? [
            'GetCurrentPrecoOffer',
            'GetCustomerWithFullAccounts',
            'AccountHistory',
          ]
        : ['GetCurrentPrecoOffer', 'AccountHistory'],
    });
    resetPrecoState();
    onContinue();
  };

  const [enrollOffer, { loading: enrollLoading, error: enrollError }] =
    useMutation(ENROLL_PRECHARGEOFF_SETTLEMENT_OFFER, { onError: () => {} });

  const [
    createPaymentPlan,
    { loading: createPlanLoading, error: createError, data: createSuccess },
  ] = useMutation(CREATE_PRECHARGEOFF_SETTLEMENT_PAYMENT_PLAN, {
    // The offer is already enrolled by this point, so a failed plan leaves the
    // agent on an offer that exists.
    onError: (error, clientOptions) => {
      precoPlanCreationErrorVar({
        offerId: clientOptions?.variables?.offerId,
        message: getPrecoPlanCreationMessage(error, { isEnrollingOffer }),
      });
      setTimeout(exitToOfferDetails, 2500);
    },
    onCompleted: () => {
      setTimeout(exitToOfferDetails, 2500);
    },
  });

  const createFailureAlert = isEnrollingOffer
    ? {
        title:
          'Program successfully enrolled but payment plan creation failed.',
        description: getPrecoErrorReason(createError),
      }
    : {
        title: getPrecoPlanCreationMessage(createError, { isEnrollingOffer }),
      };

  const planCreationAlert = enrollError
    ? { title: 'Failed to enroll offer. Please try again.' }
    : createError && createFailureAlert;

  const onConfirm = async (authorizationText) => {
    const transactions = schedule
      .filter((payment) => payment.changeType !== 'delete')
      .map(({ amount, date }) => ({ amount, date }));

    let offerId = offer.offerId;

    if (!offerId) {
      try {
        const enrollResponse = await enrollOffer({
          variables: buildEnrollVariables({ offer, customerId, accountId }),
        });
        offerId =
          enrollResponse.data.enrollPrechargeoffSettlementOffer.data.offerId;
      } catch {
        // The failure is shown through the mutation's `error` state.
        return;
      }
    }

    createPaymentPlan({
      variables: {
        offerId,
        fundingAccountId: String(fundingAccount.id),
        authorizationText,
        customerId,
        transactions,
      },
    });
  };

  return (
    <Flex vertical={true}>
      <Title level={5}>Read EFTA</Title>
      <Alert
        banner
        type="info"
        showIcon={true}
        title={`Confirm email address before continuing: ${customerInfo.email}`}
        style={{ marginBottom: 'var(--spacing)' }}
      />
      <Text type="secondary">So, to confirm:</Text>
      <Flex vertical={true} style={{ marginTop: 'var(--spacing-sm)' }}>
        <PrecoEFTAForm
          form={eftaForm}
          onFinish={onConfirm}
          fundingAccountLast4={fundingAccountLast4}
          cadence={cadence}
          schedule={schedule}
        >
          <Button
            type="primary"
            htmlType="submit"
            loading={
              enrollLoading ||
              createPlanLoading ||
              !!createSuccess ||
              !!createError
            }
          >
            {isEnrollingOffer
              ? 'Confirm and Enroll Offer'
              : 'Create Payment Plan'}
          </Button>
          {planCreationAlert && (
            <Alert
              banner
              type="error"
              title={planCreationAlert.title}
              description={planCreationAlert.description}
              style={{ marginTop: 'var(--spacing-sm)' }}
            />
          )}
        </PrecoEFTAForm>
      </Flex>
      <SectionMessage
        cover={true}
        status={STATUS.SUCCESS}
        size={SIZE.MD}
        text={
          isEnrollingOffer
            ? 'Program successfully created!'
            : 'Payment plan successfully created'
        }
        visible={!!createSuccess}
      />
    </Flex>
  );
};

export default PrecoEFTAStep;
